import { useEffect, useState } from 'react';

// Third party
import useSWRInfinite from 'swr/infinite';

// Utils
import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

// Libs
import { COMMENTS, COMMENTS_TRACKER } from '@/lib/routes/apiEndpoints';

const getKey = (pageIndex, previousPageData, activeSPID, mediaId, limit, requestParams) => {
    // Reached the end
    if (previousPageData && !previousPageData.data) return null;

    const apiUrl = mediaId
        ? injectEndpointUrlParams(COMMENTS, {
              SPID: activeSPID,
              mediaID: mediaId
          })
        : injectEndpointUrlParams(COMMENTS_TRACKER, {
              SPID: activeSPID
          });

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) return [`${apiUrl}?per_page=${limit}`, JSON.stringify(requestParams)];

    // Add the cursor to the API endpoint
    return [
        `${apiUrl}?per_page=${limit}&cursor=${previousPageData.cursor}`,
        JSON.stringify(requestParams)
    ];
};

const useComments = ({
    activeSPID,
    mediaId = null,
    limit = 20,
    params = null,
    options = {},
    shouldFetch = true
} = {}) => {
    const [requestParams, setRequestParams] = useState(params);
    const [activeSocialProfile, setActiveSocialProfile] = useState(activeSPID);

    useEffect(() => {
        setActiveSocialProfile(activeSPID);
    }, [activeSPID]);

    const {
        data: dataComments,
        size,
        setSize,
        mutate: mutateComments,
        error: commentsError
    } = useSWRInfinite(
        shouldFetch
            ? (pageIndex, previousPageData) =>
                  mediaId !== undefined
                      ? getKey(
                            pageIndex,
                            previousPageData,
                            activeSocialProfile,
                            mediaId,
                            limit,
                            requestParams
                        )
                      : null
            : null,
        ([url]) => get(url, requestParams).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateFirstPage: false, // Avoid refetching first page when loading more (per_page=20 after each paginated request)
            ...options
        }
    );

    const isLoadingInitialData = !dataComments && !commentsError;
    const isLoadingMore = size > 0 && dataComments && typeof dataComments[size - 1] === 'undefined';

    return {
        dataComments,
        loadMoreComments: () => setSize(size + 1), // Function to load more comments (load more button)
        mutateComments,
        commentsError,
        requestParams,
        setRequestParams, // Setter to change parameters in the url
        setActiveSocialProfile,
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        isLoadMore:
            dataComments && typeof dataComments[dataComments.length - 1].cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore, // Check if the load more is loading
        unreadCount: dataComments?.[0]?.summary.unread_count || 0
    };
};

export default useComments;
